import { useRecoilCallback, useSetRecoilState } from 'recoil';
import isNumber from 'lodash/isNumber';

import {
  LogResultsScrollOffset,
  logResultsScrollOffset,
  visibleCenterIndexState,
} from 'components/search/SearchLogsPage/LogResultsContainer.hooks';
import {
  downwardThresholdState,
  upwardThresholdState,
} from 'components/search/SearchLogsPage/SearchStateManager';
import {
  isScrollReadyState,
  paginatedLogRequestsState,
} from 'components/search/SearchLogsPage/SearchStateManager.state';

/**
 * When changing certain state related to search logs display, we need remember the user's current scroll position,
 * set isScrollReady to false so that scroll position tracking state is not updated in an unpredictable way, and
 * then finally set the scrollOffset so that the user will return to their previous position.
 */
export const useResetScrollState = () => {
  const setOffset = useSetRecoilState(logResultsScrollOffset);
  const setOffsetDelayed = (offset: LogResultsScrollOffset) => {
    setTimeout(() => setOffset(offset), 10);
  };
  return useRecoilCallback(
    ({ snapshot, set }) =>
      (isSortOrderChanging = false) => {
        const { index, invertIndex } = snapshot.getLoadable(visibleCenterIndexState).getValue();
        // If the sort order is not changing, use the index to reposition the user.
        if (!isSortOrderChanging && isNumber(index)) {
          set(isScrollReadyState, false);
          set(logResultsScrollOffset, { align: 'center', index });
        }
        // If the sort order is changing, we need to use the invertIndex instead of the index to reposition the user.
        // From empirical testing, we also know that a slight delay is required for this offset to work.
        if (isSortOrderChanging && isNumber(invertIndex)) {
          set(isScrollReadyState, false);
          setOffsetDelayed({ align: 'center', index: invertIndex });
        }
        // If the sort order is changing, we also need to reverse the pagination list states.
        if (isSortOrderChanging) {
          set(downwardThresholdState, (list) => [ ...list ].reverse());
          set(upwardThresholdState, (list) => [ ...list ].reverse());
          set(paginatedLogRequestsState, (list) => [ ...list ].reverse());
        }
      },
    [],
  );
};
